import { PureComponent, forwardRef, RefObject } from 'react'

// Components
import {
  Button,
  Link,
  IconCloseFat,
  IconNotificationCredits,
  IconNotificationCredit,
  IconArrow,
  IconNotificationInviteConfirmed,
  IconNotification,
  IconNotificationPreparingDownload,
  IconNotificationReferral,
  IconNotificationReportIncome,
  IconSmiley,
} from '@tracklib/kit'
import CoverArt from 'components/coverart'
import CollectionCover from 'components/collection/cover'

// Types
import { Notification as NotificationType } from 'types'

import styles from './notification.css'

type NotificationProps = {
  notification: NotificationType
  showClose?: boolean
  onSeen: (notification: NotificationType) => void
  innerRef: RefObject<unknown>
}

class Notification extends PureComponent<NotificationProps> {
  static defaultProps = {
    showClose: true,
  }
  hideTimeout: TimeoutID | null | undefined = null

  componentDidMount() {
    // flash note
    const { notification, onSeen } = this.props
    if (notification.persistence === 3) {
      this.hideTimeout = setTimeout(() => onSeen(notification), 5000)
    }
  }

  handleHover = () => {
    if (this.props.notification.persistence === 3) {
      clearTimeout(this.hideTimeout)
    }
  }

  handleEndHover = () => {
    const { notification, onSeen } = this.props
    if (notification.persistence === 3) {
      this.hideTimeout = setTimeout(() => onSeen(notification), 2000)
    }
  }

  handleOnSeen = () => {
    this.props.onSeen(this.props.notification)
  }

  renderIcon = () => {
    const { notification } = this.props

    const { cover, icon_url: icon, collection } = notification
    if (icon === 'cover' && cover) {
      return <CoverArt cover={cover} className={styles.cover} />
    }
    if (icon === 'collectionCover' && notification.collection) {
      return (
        <div className={styles.cover}>
          <CollectionCover covers={collection?.covers} />
        </div>
      )
    }
    switch (icon) {
      case 'credit':
        return <IconNotificationCredits />
      case 'download':
        return <IconNotificationPreparingDownload />
      case 'invite':
        return <IconNotificationInviteConfirmed />
      case 'notification':
        return <IconNotificationCredit />
      case 'referral':
        return <IconNotificationReferral />
      case 'report-income':
        return <IconNotificationReportIncome />
      case 'smiley':
        return <IconSmiley />
      default:
        return <IconNotification />
    }
  }

  renderButton = () => {
    const { notification } = this.props
    const buttonUrl = notification.button_url
    const buttonText = notification.button_text
    if (buttonUrl.startsWith('?modal')) {
      const modal = buttonUrl.replace('?modal=', '')
      return (
        <Link to={modal} modal onClick={this.handleOnSeen} className={styles.button}>
          <span>
            {buttonText} <IconArrow className={styles.buttonArrow} />
          </span>
        </Link>
      )
    }
    return (
      <Button to={buttonUrl} onClick={this.handleOnSeen} className={styles.button}>
        {buttonText} <IconArrow className={styles.buttonArrow} />
      </Button>
    )
  }

  render() {
    const { notification, showClose, innerRef } = this.props
    const { title } = notification
    const { body } = notification
    return (
      <div
        className={styles.notification}
        onMouseEnter={this.handleHover}
        onMouseLeave={this.handleEndHover}
        ref={innerRef}
        datatesting={notification.datatesting}
      >
        {showClose && (
          <button className={styles.close} onClick={this.handleOnSeen} datatesting="notification_close_button">
            <IconCloseFat />
          </button>
        )}
        <div className={styles.icon}>{this.renderIcon()}</div>
        <div className={styles.info} datatesting="notification_info">
          {title && <p className={styles.title}>{title}</p>}
          {body && <p className={styles.body}>{body}</p>}
          {notification.button_text && notification.button_url && this.renderButton()}
        </div>
      </div>
    )
  }
}

export { Notification as Component }
export default forwardRef<NotificationProps, Notification>((props, ref) => <Notification {...props} innerRef={ref} />)
